import type { ContratoDaObra, SituacaoDeContrato } from '@obras/shared';
import {
  formatCnpj,
  formatCurrency,
  formatDate,
  formatDays,
  formatPercent,
  ratio,
  SITUACAO_DE_CONTRATO_LABELS,
} from '@obras/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArrowUp,
  ArrowUpDown,
  Building2,
  CalendarDays,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  Clock,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import type { ReactNode } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Badge, type Tone } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { Skeleton } from '../../components/ui/Feedback.js';
import { cn } from '../../lib/cn.js';
import { COLUNAS_DA_PLANILHA, type ColunaDaPlanilha, type Ordem } from './planilha.js';

/** Até onde a fila de entrada das linhas vai: da nona em diante, todas juntas. */
const LINHAS_ESCALONADAS = 8;
const PASSO = 0.03;

/** O corpo entra e sai como um bloco, na troca de escopo, de aba ou de ordem. */
const CORPO: Variants = {
  hidden: {},
  shown: {},
  gone: { opacity: 0, transition: { duration: 0.14, ease: EASE_OUT_QUINT } },
};

/** Cada linha sobe 8px e assenta no seu passo da fila — o gesto da lista de obras. */
const LINHA: Variants = {
  hidden: { opacity: 0, y: 8 },
  shown: (indice: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.32,
      ease: EASE_OUT_QUINT,
      delay: Math.min(indice, LINHAS_ESCALONADAS) * PASSO,
    },
  }),
};

/** O selo e o ícone de cada situação — os mesmos na aba e na linha. */
export const APARENCIA_DA_SITUACAO: Record<
  SituacaoDeContrato,
  { readonly tom: Tone; readonly icone: LucideIcon }
> = {
  vencido: { tom: 'danger', icone: CircleAlert },
  a_vencer: { tom: 'warning', icone: Clock },
  vigente: { tom: 'success', icone: CircleCheck },
  sem_vigencia: { tom: 'muted', icone: CircleDashed },
  encerrado: { tom: 'muted', icone: Archive },
};

/*
 * Obra e contratada são as colunas que cedem: dividem o que as outras deixam
 * (`max-w-0` tira delas a largura mínima do texto) e cortam o nome com
 * reticências. Situação, vigência e valores nunca quebram nem encolhem.
 */
const COLUNA: Record<
  ColunaDaPlanilha,
  { readonly rotulo: string; readonly direita?: boolean; readonly largura?: string }
> = {
  obra: { rotulo: 'Obra', largura: 'w-[58%] max-w-0' },
  contratada: { rotulo: 'Contratada', largura: 'w-[42%] max-w-0' },
  situacao: { rotulo: 'Situação' },
  vigencia: { rotulo: 'Vigência' },
  valor: { rotulo: 'Valor atual', direita: true },
  saldo: { rotulo: 'Saldo', direita: true },
};

function Cabecalho({
  ordem,
  onOrdenar,
}: {
  ordem?: Ordem | undefined;
  onOrdenar?: ((coluna: ColunaDaPlanilha) => void) | undefined;
}) {
  return (
    <thead className="bg-sunken">
      <tr className="text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase *:border-b-2 *:border-line-2">
        {COLUNAS_DA_PLANILHA.map((coluna) => {
          const { rotulo, direita, largura } = COLUNA[coluna];
          const ativa = ordem?.coluna === coluna;

          return (
            <th
              key={coluna}
              scope="col"
              aria-sort={
                ativa ? (ordem?.sentido === 'asc' ? 'ascending' : 'descending') : undefined
              }
              className={cn(
                'px-4 py-3 font-medium whitespace-nowrap',
                direita ? 'text-right' : 'text-left',
                largura,
              )}
            >
              {onOrdenar ? (
                /*
                 * Clicar no título ordena por ele; de novo, inverte. A seta da
                 * ativa gira entre os sentidos; nas outras, a dupla aparece sob
                 * o ponteiro, dizendo que dá para ordenar por ali também.
                 */
                <button
                  type="button"
                  onClick={() => onOrdenar(coluna)}
                  className={cn(
                    'group inline-flex items-center gap-1.5 uppercase transition-colors hover:text-ink',
                    direita && 'flex-row-reverse',
                    ativa && 'text-ink',
                  )}
                >
                  {rotulo}
                  {ativa ? (
                    <motion.span
                      aria-hidden
                      initial={false}
                      animate={{ rotate: ordem?.sentido === 'asc' ? 0 : 180 }}
                      transition={{ duration: 0.2, ease: EASE_OUT_QUINT }}
                      className="grid text-primary"
                    >
                      <ArrowUp size={12} strokeWidth={2} />
                    </motion.span>
                  ) : (
                    <ArrowUpDown
                      size={12}
                      strokeWidth={2}
                      aria-hidden
                      className="opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60"
                    />
                  )}
                </button>
              ) : (
                rotulo
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

/**
 * O prazo, embaixo do selo, completando-o: "Vencido · há 6 dias", "A vencer ·
 * em 45 dias", "Vigente · faltam 247 dias". Sem data de término não há prazo.
 */
function textoDoPrazo(contrato: ContratoDaObra): string | null {
  const dias = contrato.diasRestantes ?? 0;
  switch (contrato.situacao) {
    case 'vencido':
      return `há ${formatDays(-dias)}`;
    case 'a_vencer':
      return dias === 0 ? 'vence hoje' : `em ${formatDays(dias)}`;
    case 'vigente':
      return `faltam ${formatDays(dias)}`;
    case 'sem_vigencia':
      return null;
    case 'encerrado':
      return contrato.obraStatus === 'cancelada' ? 'obra cancelada' : 'obra concluída';
  }
}

export function SeloDaSituacao({ situacao }: { situacao: SituacaoDeContrato }) {
  const { tom, icone } = APARENCIA_DA_SITUACAO[situacao];
  return (
    <Badge tone={tom} icon={icone}>
      {SITUACAO_DE_CONTRATO_LABELS[situacao]}
    </Badge>
  );
}

const COR_DA_REGUA: Record<SituacaoDeContrato, string> = {
  vencido: 'bg-danger',
  a_vencer: 'bg-warning',
  vigente: 'bg-ink-3/60',
  sem_vigencia: 'bg-ink-3/60',
  encerrado: 'bg-ink-3/60',
};

/**
 * A régua da vigência: quanto do período já passou, na cor da situação. Enche
 * ao entrar, um passo depois da linha. Só no contrato em curso e com as duas
 * datas — o encerrado e o sem término não têm o que medir.
 */
function Regua({ contrato, indice }: { contrato: ContratoDaObra; indice: number }) {
  if (
    contrato.contractStart === null ||
    contrato.contractEnd === null ||
    contrato.situacao === 'encerrado' ||
    contrato.situacao === 'sem_vigencia'
  ) {
    return null;
  }

  return (
    <span aria-hidden className="mt-2 block h-1 w-full bg-sunken">
      <motion.span
        className={cn('block h-full origin-left', COR_DA_REGUA[contrato.situacao])}
        style={{ width: `${Math.round(contrato.vigenciaDecorrida * 100)}%` }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{
          duration: 0.5,
          ease: EASE_OUT_QUINT,
          delay: 0.12 + Math.min(indice, LINHAS_ESCALONADAS) * PASSO,
        }}
      />
    </span>
  );
}

function Vigencia({ contrato }: { contrato: ContratoDaObra }) {
  if (!contrato.contractStart && !contrato.contractEnd)
    return <span className="text-ink-3">—</span>;
  return (
    <>
      {formatDate(contrato.contractStart)}
      <span className="mx-1.5 text-ink-3">→</span>
      {formatDate(contrato.contractEnd)}
    </>
  );
}

/** Um dado com o ícone no lugar do rótulo; o rótulo vai para a dica e o leitor de tela. */
function ComIcone({
  icone: Icone,
  rotulo,
  children,
  className,
}: {
  icone: LucideIcon;
  rotulo: string;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <span title={rotulo} className={cn('flex min-w-0 items-center gap-1.5', className)}>
      <Icone size={12} strokeWidth={1.75} className="shrink-0" aria-hidden />
      <span className="sr-only">{rotulo}:</span>
      <span className="truncate">{children}</span>
    </span>
  );
}

const contarContratos = (total: number): string =>
  total === 1 ? '1 contrato' : `${total} contratos`;

function titulo(contrato: ContratoDaObra): string | undefined {
  const empresa = contrato.company;
  if (!empresa) return undefined;
  return empresa.cnpj ? `${empresa.name} — CNPJ ${formatCnpj(empresa.cnpj)}` : empresa.name;
}

/**
 * A planilha de contratos (design.md §8, Contratos): uma linha por obra, a
 * situação logo depois de quem é quem, os valores na ponta direita, a ordem
 * pelo título da coluna e o total no pé.
 *
 * A linha inteira leva à obra e escurece sob o ponteiro, como na lista de
 * obras; o nome é o link de verdade, para o teclado e para abrir em outra aba.
 * Nada se edita aqui: o contrato é o da obra, e corrige-se na obra.
 *
 * No celular a grade não cabe: cada contrato vira um bloco, com os mesmos dados
 * e na mesma ordem.
 */
export function PlanilhaDeContratos({
  contratos,
  ordem,
  onOrdenar,
  chave,
}: {
  contratos: readonly ContratoDaObra[];
  ordem: Ordem;
  onOrdenar: (coluna: ColunaDaPlanilha) => void;
  /** Muda com escopo, aba e ordem: a planilha troca em bloco. A busca filtra no lugar. */
  chave: string;
}) {
  const navigate = useNavigate();
  const totalValor = contratos.reduce((soma, contrato) => soma + contrato.currentValueCents, 0);
  const totalSaldo = contratos.reduce((soma, contrato) => soma + contrato.saldoCents, 0);

  return (
    <>
      <Card square className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[920px]">
          <caption className="sr-only">Contratos das obras</caption>
          <Cabecalho ordem={ordem} onOrdenar={onOrdenar} />

          <AnimatePresence mode="wait" initial={false}>
            <motion.tbody key={chave} variants={CORPO} initial="hidden" animate="shown" exit="gone">
              {contratos.map((contrato, indice) => {
                const prazo = textoDoPrazo(contrato);
                return (
                  <motion.tr
                    key={contrato.obraId}
                    variants={LINHA}
                    custom={indice}
                    onClick={(evento) => {
                      // O link do nome já leva à obra; a linha cobre o resto.
                      if ((evento.target as HTMLElement).closest('a')) return;
                      void navigate({ to: '/obras/$obraId', params: { obraId: contrato.obraId } });
                    }}
                    className="cursor-pointer border-t border-line align-top transition-colors duration-100 hover:bg-sunken"
                  >
                    <td className="max-w-0 px-4 py-3">
                      <Link
                        to="/obras/$obraId"
                        params={{ obraId: contrato.obraId }}
                        title={contrato.obraName}
                        className="block truncate text-[14px] font-medium tracking-tight"
                      >
                        {contrato.obraName}
                      </Link>
                      <span className="mt-0.5 flex min-w-0 items-center gap-2 text-[12px] text-ink-3">
                        <span className="shrink-0 font-mono">
                          {contrato.contractNumber ?? 'sem número'}
                        </span>
                        <span aria-hidden className="shrink-0">
                          ·
                        </span>
                        <ComIcone icone={UserRound} rotulo="Fiscal responsável">
                          {contrato.responsibleFiscal?.name ?? 'sem fiscal'}
                        </ComIcone>
                      </span>
                    </td>

                    <td className="max-w-0 px-4 py-3 text-[13px]">
                      <span className="block truncate" title={titulo(contrato)}>
                        {contrato.company?.name ?? <span className="text-ink-3">—</span>}
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <SeloDaSituacao situacao={contrato.situacao} />
                      {prazo ? (
                        <span className="tabular mt-1 block text-[12px] text-ink-2">{prazo}</span>
                      ) : null}
                    </td>

                    <td className="tabular px-4 py-3 text-[13px] whitespace-nowrap">
                      <Vigencia contrato={contrato} />
                      <Regua contrato={contrato} indice={indice} />
                    </td>

                    <td className="numeral px-4 py-3 text-right text-[13px] whitespace-nowrap">
                      {formatCurrency(contrato.currentValueCents)}
                    </td>

                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span className="numeral block text-[13px]">
                        {formatCurrency(contrato.saldoCents)}
                      </span>
                      <span className="tabular mt-0.5 block text-[12px] text-ink-3">
                        {formatPercent(ratio(contrato.paidValueCents, contrato.currentValueCents))}{' '}
                        pago
                      </span>
                    </td>
                  </motion.tr>
                );
              })}
            </motion.tbody>
          </AnimatePresence>

          {/* O total da planilha à vista: o que a busca e a aba deixaram passar. */}
          <tfoot>
            <tr className="border-t-2 border-line-2 bg-surface-2 text-[13px]">
              <td colSpan={4} className="px-4 py-3 font-medium">
                {contarContratos(contratos.length)}
              </td>
              <td className="numeral px-4 py-3 text-right font-medium whitespace-nowrap">
                {formatCurrency(totalValor)}
              </td>
              <td className="numeral px-4 py-3 text-right font-medium whitespace-nowrap">
                {formatCurrency(totalSaldo)}
              </td>
            </tr>
          </tfoot>
        </table>
      </Card>

      <Card square className="md:hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.ul
            key={chave}
            variants={CORPO}
            initial="hidden"
            animate="shown"
            exit="gone"
            aria-label="Contratos das obras"
          >
            {contratos.map((contrato, indice) => (
              <ContratoEmBloco key={contrato.obraId} contrato={contrato} indice={indice} />
            ))}
          </motion.ul>
        </AnimatePresence>

        <div className="border-t-2 border-line-2 bg-surface-2 px-4 py-3 text-[13px]">
          <p className="font-medium">{contarContratos(contratos.length)}</p>
          <dl className="mt-1.5 grid grid-cols-2 gap-3">
            <Total rotulo="Valor atual" valor={totalValor} />
            <Total rotulo="Saldo" valor={totalSaldo} />
          </dl>
        </div>
      </Card>
    </>
  );
}

function Total({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div>
      <dt className="text-[11px] tracking-[0.04em] text-ink-3 uppercase">{rotulo}</dt>
      <dd className="numeral font-medium">{formatCurrency(valor)}</dd>
    </div>
  );
}

/** O contrato no celular: situação e número no alto, a obra, quem é quem, a vigência e os valores. */
function ContratoEmBloco({ contrato, indice }: { contrato: ContratoDaObra; indice: number }) {
  const prazo = textoDoPrazo(contrato);

  return (
    <motion.li variants={LINHA} custom={indice} className="border-t border-line first:border-t-0">
      <Link
        to="/obras/$obraId"
        params={{ obraId: contrato.obraId }}
        className="block px-4 py-3.5 transition-colors duration-100 hover:bg-sunken"
      >
        <span className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <SeloDaSituacao situacao={contrato.situacao} />
            {prazo ? (
              <span className="tabular truncate text-[12px] text-ink-2">{prazo}</span>
            ) : null}
          </span>
          <span className="shrink-0 font-mono text-[12px] text-ink-3">
            {contrato.contractNumber ?? 'sem número'}
          </span>
        </span>

        <span className="mt-2 line-clamp-2 text-[14px] font-medium tracking-tight">
          {contrato.obraName}
        </span>

        <span className="mt-1.5 flex flex-col gap-1 text-[12px] text-ink-3">
          <ComIcone icone={Building2} rotulo="Contratada">
            {contrato.company?.name ?? 'sem contratada'}
          </ComIcone>
          <ComIcone icone={UserRound} rotulo="Fiscal responsável">
            {contrato.responsibleFiscal?.name ?? 'sem fiscal'}
          </ComIcone>
          <ComIcone icone={CalendarDays} rotulo="Vigência" className="tabular">
            <Vigencia contrato={contrato} />
          </ComIcone>
        </span>

        <span className="mt-2.5 grid grid-cols-2 gap-3 text-[13px]">
          <span>
            <span className="block text-[11px] tracking-[0.04em] text-ink-3 uppercase">
              Valor atual
            </span>
            <span className="numeral block">{formatCurrency(contrato.currentValueCents)}</span>
          </span>
          <span>
            <span className="block text-[11px] tracking-[0.04em] text-ink-3 uppercase">Saldo</span>
            <span className="numeral block">{formatCurrency(contrato.saldoCents)}</span>
            <span className="tabular block text-[12px] text-ink-3">
              {formatPercent(ratio(contrato.paidValueCents, contrato.currentValueCents))} pago
            </span>
          </span>
        </span>
      </Link>
    </motion.li>
  );
}

/** A planilha enquanto os contratos não chegam: as mesmas colunas, as mesmas alturas. */
export function PlanilhaDeContratosEsqueleto() {
  return (
    <>
      <Card square className="hidden overflow-x-auto md:block" aria-hidden>
        <table className="w-full min-w-[920px]">
          <Cabecalho />
          <tbody>
            {Array.from({ length: 8 }, (_, linha) => (
              <tr key={linha} className="border-t border-line">
                <td className="max-w-0 px-4 py-3">
                  <Skeleton className="h-3.5 w-4/5 rounded-none" />
                  <Skeleton className="mt-1.5 h-3 w-2/5 rounded-none" />
                </td>
                <td className="max-w-0 px-4 py-3">
                  <Skeleton className="h-3.5 w-3/4 rounded-none" />
                </td>
                <td className="px-4 py-3">
                  <Skeleton className="h-5 w-20 rounded-none" />
                  <Skeleton className="mt-1.5 h-3 w-16 rounded-none" />
                </td>
                <td className="px-4 py-3">
                  <Skeleton className="h-3.5 w-40 rounded-none" />
                  <Skeleton className="mt-2 h-1 w-full rounded-none" />
                </td>
                {[0, 1].map((coluna) => (
                  <td key={coluna} className="px-4 py-3">
                    <Skeleton className="ml-auto h-3.5 w-24 rounded-none" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card square className="md:hidden" aria-hidden>
        {Array.from({ length: 5 }, (_, bloco) => (
          <div key={bloco} className="border-t border-line px-4 py-3.5 first:border-t-0">
            <Skeleton className="h-5 w-24 rounded-none" />
            <Skeleton className="mt-2.5 h-3.5 w-4/5 rounded-none" />
            <Skeleton className="mt-2 h-3 w-1/2 rounded-none" />
            <Skeleton className="mt-1.5 h-3 w-2/5 rounded-none" />
            <Skeleton className="mt-3 h-3.5 w-3/4 rounded-none" />
          </div>
        ))}
      </Card>
    </>
  );
}
